import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Search, Shield, Zap, Clock, Lock, ExternalLink, Fuel, Gauge, CalendarDays, Car, FileText, AlertTriangle, ChevronRight, Bell, User as UserIcon, Sun, Moon, Share2, Copy, MessageCircle, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import Footer from '@/components/Footer';
import AuthModal from '@/components/AuthModal';
import ChatWidget from '@/components/ChatWidget';
import SetReminderModal from '@/components/SetReminderModal';
import WaitlistForm from '@/components/WaitlistForm';
import { useTheme } from '@/contexts/ThemeContext';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${window.location.origin}/api`;

function LandingPage({ user, onLogout }) {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [authAction, setAuthAction] = useState(null);
  const [reg, setReg] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [buyLoading, setBuyLoading] = useState(false);
  const [blogPosts, setBlogPosts] = useState([]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sharedReg = urlParams.get('reg');
    if (sharedReg) {
      setReg(sharedReg.toUpperCase());
      setTimeout(() => doSearch(sharedReg), 300);
      window.history.replaceState({}, document.title, '/');
    }
    const reportSuccess = urlParams.get('report_success');
    const purchaseReg = urlParams.get('registration');
    if (reportSuccess && user) {
      window.history.replaceState({}, document.title, '/');
      confirmPurchaseAndGenerate(reportSuccess, purchaseReg);
    }
  }, [user]);

  useEffect(() => {
    axios.get(`${API}/blog/posts?limit=3`).then(res => setBlogPosts(res.data.posts || [])).catch(() => {});
  }, []);

  const doSearch = async (registration) => {
    const r = registration || reg;
    if (!r.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const response = await axios.post(`${API}/vehicle/lookup-guest`, {
        registration: r.toUpperCase().replace(/\s/g, '')
      });
      setResult(response.data);
      toast.success('Vehicle found!');
      setTimeout(() => {
        document.getElementById('results')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Vehicle not found. Please check the registration.');
      } else {
        toast.error(error.response?.data?.detail || 'Search failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    doSearch();
  };

  const confirmPurchaseAndGenerate = async (purchaseId, registration) => {
    try {
      toast.loading('Confirming payment...');
      await axios.post(`${API}/reports/confirm-purchase/${purchaseId}`, {}, { withCredentials: true });
      if (registration) {
        toast.loading('Generating report...');
        const res = await axios.post(`${API}/reports/generate`, { registration }, { withCredentials: true });
        toast.dismiss();
        toast.success('Report generated!');
        navigate(`/report/${res.data.report.id}`);
      } else {
        toast.dismiss();
        toast.success('Credits added to your account!');
        navigate('/account');
      }
    } catch (err) {
      toast.dismiss();
      toast.error(err.response?.data?.detail || 'Error confirming purchase');
    }
  };

  const handleBuyReport = () => {
    // Paid full history reports are temporarily disabled. The waitlist form on the
    // homepage is the only paid-report entry point until PAID_REPORTS_ENABLED is flipped on.
    toast.message("The full history check is coming soon — join the waitlist below.");
  };

  const handleGenerateReport = async () => {
    if (!user) {
      setAuthAction('generate');
      setShowAuthModal(true);
      return;
    }
    setBuyLoading(true);
    try {
      const res = await axios.post(`${API}/reports/generate`, {
        registration: result.registration
      }, { withCredentials: true });
      toast.success('Report generated!');
      navigate(`/report/${res.data.report.id}`);
    } catch (err) {
      if (err.response?.status === 402) {
        toast.error('No credits remaining. Purchase a report pack.');
      } else {
        toast.error(err.response?.data?.detail || 'Error generating report');
      }
      setBuyLoading(false);
    }
  };

  const fmt = (dateStr) => {
    if (!dateStr) return 'N/A';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const daysUntil = (dateStr) => {
    if (!dateStr) return null;
    const diff = Math.ceil((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const taxColor = (status) => {
    if (!status) return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
    const s = status.toLowerCase();
    if (s === 'taxed') return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400';
    if (s === 'sorn') return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400';
    return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
  };

  const motColor = (status) => {
    if (!status) return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
    const s = status.toLowerCase();
    if (s === 'valid') return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400';
    if (s === 'not valid') return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
    return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400';
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 transition-colors duration-300">
      {/* Header */}
      <header className="border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 transition-colors">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <img
            src="https://customer-assets.emergentagent.com/job_carinfo-lookup/artifacts/kwmtzzvb_image.png"
            alt="IsItTaxed.co.uk"
            className="h-12 w-auto"
          />
          <div className="flex items-center gap-3">
            <button
              onClick={toggleTheme}
              className="p-2 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              data-testid="theme-toggle-btn"
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
            {user ? (
              <>
                <Button
                  onClick={() => navigate('/account')}
                  variant="outline"
                  size="sm"
                  className="border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                  data-testid="my-account-btn"
                >
                  <UserIcon className="h-4 w-4 mr-1" /> My Account
                </Button>
                <Button
                  onClick={onLogout}
                  variant="ghost"
                  size="sm"
                  className="text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                  data-testid="header-logout-btn"
                >
                  Sign Out
                </Button>
              </>
            ) : (
              <Button
                onClick={() => setShowAuthModal(true)}
                size="sm"
                className="bg-yellow-500 hover:bg-yellow-400 text-gray-900 font-semibold"
                data-testid="header-login-btn"
              >
                Sign In
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="container mx-auto px-4 pt-16 pb-8 text-center">
        <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-4 transition-colors" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Is your car costing you more than it should?
        </h1>
        <p className="text-gray-500 dark:text-gray-400 text-base sm:text-lg mb-10 max-w-xl mx-auto">
          Check any UK vehicle in seconds — tax, MOT, annual cost, fuel economy. Free, instant, unlimited.
        </p>

        <form onSubmit={handleSearch} className="max-w-lg mx-auto mb-6">
          <div className="uk-plate-container" style={{ justifyContent: 'center' }}>
            <div className="uk-plate relative">
              <input
                type="text"
                value={reg}
                onChange={(e) => setReg(e.target.value.toUpperCase())}
                placeholder="ENTER REG"
                className="uk-plate-input"
                maxLength={8}
                required
                data-testid="reg-input"
              />
            </div>
          </div>
          <Button
            type="submit"
            size="lg"
            className="mt-5 bg-yellow-500 hover:bg-yellow-400 text-gray-900 font-bold text-lg h-14 px-10 w-full sm:w-auto"
            disabled={loading}
            data-testid="search-btn"
          >
            {loading ? (
              <><Search className="mr-2 h-5 w-5 animate-spin" />Checking...</>
            ) : (
              <><Search className="mr-2 h-5 w-5" />Check Vehicle Free</>
            )}
          </Button>
        </form>
        {!result && (
          <p className="text-gray-400 dark:text-gray-500 text-sm">No sign-up needed. Completely free. Unlimited searches.</p>
        )}
      </section>

      {/* ==================== RESULTS ==================== */}
      {result && (
        <section id="results" className="container mx-auto px-4 pb-16">
          <div className="max-w-4xl mx-auto">
            {/* Registration plate header */}
            <div className="text-center mb-8">
              <div className="inline-block bg-gradient-to-b from-yellow-300 to-yellow-500 px-10 py-4 rounded-lg border-4 border-gray-900 shadow-xl">
                <span className="font-black text-3xl sm:text-4xl text-gray-900 tracking-widest" style={{ fontFamily: 'Charles Wright, monospace' }}>
                  {result.registration}
                </span>
              </div>
              <p className="text-gray-500 dark:text-gray-400 mt-3 text-lg">
                {result.year_of_manufacture} {result.make} {result.model} — {result.colour}
              </p>
              {/* Share buttons */}
              <p className="text-gray-400 dark:text-gray-500 text-xs mt-3 mb-2">Know someone buying a car? This could save them thousands.</p>
              <div className="flex items-center justify-center gap-2" data-testid="share-bar">
                <button
                  onClick={() => {
                    const url = `${window.location.origin}?reg=${result.registration}`;
                    const text = `Worth checking this ${result.year_of_manufacture} ${result.make} ${result.model || ''} before you buy — Tax: ${result.tax_status}, MOT: ${result.mot_status}`;
                    const waUrl = `https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`;
                    window.open(waUrl, '_blank');
                    toast.success('Opening WhatsApp...');
                  }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/50 text-sm transition-colors"
                  data-testid="share-whatsapp-btn"
                >
                  <MessageCircle className="h-4 w-4" /> Send to a friend
                </button>
                <button
                  onClick={() => {
                    const url = `${window.location.origin}?reg=${result.registration}`;
                    navigator.clipboard.writeText(url);
                    toast.success('Link copied!');
                  }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 text-sm transition-colors"
                  data-testid="share-copy-btn"
                >
                  <Copy className="h-4 w-4" /> Copy Link
                </button>
                {typeof navigator.share === 'function' && (
                  <button
                    onClick={() => {
                      const url = `${window.location.origin}?reg=${result.registration}`;
                      navigator.share({
                        title: `${result.registration} - IsItTaxed`,
                        text: `Worth checking this ${result.year_of_manufacture} ${result.make} ${result.model || ''} — Tax: ${result.tax_status}, MOT: ${result.mot_status}`,
                        url
                      }).catch(() => {});
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-900/50 text-sm transition-colors"
                    data-testid="share-native-btn"
                  >
                    <Share2 className="h-4 w-4" /> Share
                  </button>
                )}
              </div>
            </div>

            {/* Vehicle image */}
            {result.image_url ? (
              <div className="mb-8 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-800 max-w-md mx-auto">
                <img
                  src={result.image_url}
                  alt={`${result.make} ${result.model}`}
                  className="w-full h-56 object-cover"
                  onError={(e) => { e.target.style.display = 'none'; }}
                  data-testid="vehicle-image"
                />
                <p className="text-[11px] text-gray-400 dark:text-gray-600 text-center py-1.5 bg-gray-50 dark:bg-gray-900">
                  Image is a representation of the model, not the actual vehicle
                </p>
              </div>
            ) : (
              <div className="mb-8 rounded-xl border border-gray-200 dark:border-gray-800 max-w-md mx-auto flex items-center justify-center h-40 bg-gray-100 dark:bg-gray-900" data-testid="image-not-available">
                <p className="text-gray-400 dark:text-gray-600 text-sm">Image not available</p>
              </div>
            )}

            {/* Status badges */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className={`rounded-xl p-5 text-center ${taxColor(result.tax_status)}`} data-testid="tax-status-card">
                <p className="text-xs font-semibold uppercase tracking-wider opacity-70 mb-1">Tax Status</p>
                <p className="text-2xl font-bold">{result.tax_status || 'Unknown'}</p>
                <p className="text-sm mt-1">
                  {(() => {
                    const days = daysUntil(result.tax_due_date);
                    if (days !== null && days >= 0 && days <= 30) return <span className="font-semibold">Expires in {days} days</span>;
                    if (days !== null && days < 0) return <span className="font-semibold">Expired {Math.abs(days)} days ago</span>;
                    return <>Expires {fmt(result.tax_due_date)}</>;
                  })()}
                </p>
              </div>
              <div className={`rounded-xl p-5 text-center ${motColor(result.mot_status)}`} data-testid="mot-status-card">
                <p className="text-xs font-semibold uppercase tracking-wider opacity-70 mb-1">MOT Status</p>
                <p className="text-2xl font-bold">{result.mot_status || 'Unknown'}</p>
                <p className="text-sm mt-1">
                  {(() => {
                    const days = daysUntil(result.mot_expiry_date);
                    if (days !== null && days >= 0 && days <= 30) return <span className="font-semibold">Expires in {days} days</span>;
                    if (days !== null && days < 0) return <span className="font-semibold">Expired {Math.abs(days)} days ago</span>;
                    return <>Expires {fmt(result.mot_expiry_date)}</>;
                  })()}
                </p>
              </div>
            </div>

            {/* Free info grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              {/* Annual Tax Cost */}
              <Card className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800">
                <CardContent className="p-5">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="bg-yellow-500/10 p-2 rounded-lg">
                      <CalendarDays className="h-5 w-5 text-yellow-500" />
                    </div>
                    <span className="text-gray-500 dark:text-gray-400 text-sm">Annual Tax Cost</span>
                  </div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white" data-testid="annual-tax-cost">
                    {result.annual_tax_cost != null ? `£${result.annual_tax_cost}/yr` : 'N/A'}
                  </p>
                  {result.tax_band && (
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Band {result.tax_band}</p>
                  )}
                </CardContent>
              </Card>

              {/* Estimated MPG */}
              <Card className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800">
                <CardContent className="p-5">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="bg-emerald-500/10 p-2 rounded-lg">
                      <Fuel className="h-5 w-5 text-emerald-500" />
                    </div>
                    <span className="text-gray-500 dark:text-gray-400 text-sm">Estimated MPG</span>
                  </div>
                  {result.estimated_mpg ? (
                    <>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white" data-testid="estimated-mpg">
                        {result.estimated_mpg.combined} mpg
                      </p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                        Urban {result.estimated_mpg.urban} / Extra-urban {result.estimated_mpg.extra_urban}
                      </p>
                    </>
                  ) : (
                    <p className="text-2xl font-bold text-gray-400 dark:text-gray-500">N/A</p>
                  )}
                </CardContent>
              </Card>

              {/* CO2 Emissions */}
              <Card className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800">
                <CardContent className="p-5">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="bg-blue-500/10 p-2 rounded-lg">
                      <Gauge className="h-5 w-5 text-blue-500" />
                    </div>
                    <span className="text-gray-500 dark:text-gray-400 text-sm">CO2 Emissions</span>
                  </div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white" data-testid="co2-emissions">
                    {result.co2_emissions ? `${result.co2_emissions} g/km` : 'N/A'}
                  </p>
                </CardContent>
              </Card>

              {/* Engine & Fuel */}
              <Card className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800">
                <CardContent className="p-5">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="bg-orange-500/10 p-2 rounded-lg">
                      <Car className="h-5 w-5 text-orange-500" />
                    </div>
                    <span className="text-gray-500 dark:text-gray-400 text-sm">Engine</span>
                  </div>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">
                    {result.engine_capacity ? `${(result.engine_capacity / 1000).toFixed(1)}L` : 'N/A'} {result.fuel_type}
                  </p>
                </CardContent>
              </Card>

              {/* Year */}
              <Card className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800">
                <CardContent className="p-5">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="bg-purple-500/10 p-2 rounded-lg">
                      <Clock className="h-5 w-5 text-purple-500" />
                    </div>
                    <span className="text-gray-500 dark:text-gray-400 text-sm">First Registered</span>
                  </div>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">
                    {result.month_of_first_registration || result.year_of_manufacture || 'N/A'}
                  </p>
                </CardContent>
              </Card>

              {/* Check Insurance */}
              <Card className="bg-white dark:bg-gray-900/60 border-yellow-400 dark:border-yellow-600/30 border-2 cursor-pointer hover:border-yellow-500 dark:hover:border-yellow-500/50 transition-all"
                    onClick={() => window.open('https://ownvehicle.askmid.com/', '_blank')}>
                <CardContent className="p-5">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="bg-yellow-500/10 p-2 rounded-lg">
                      <Shield className="h-5 w-5 text-yellow-500" />
                    </div>
                    <span className="text-gray-500 dark:text-gray-400 text-sm">Insurance</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-base font-semibold text-yellow-600 dark:text-yellow-500" data-testid="check-insurance-btn">Check if Insured</p>
                    <ExternalLink className="h-4 w-4 text-yellow-600 dark:text-yellow-500" />
                  </div>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Via askMID — official UK database</p>
                </CardContent>
              </Card>
            </div>

            {/* Set Reminder prompt */}
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-5 mb-8 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="bg-blue-500/10 p-3 rounded-full">
                  <Bell className="h-6 w-6 text-blue-400" />
                </div>
                <div>
                  <p className="text-gray-900 dark:text-white font-semibold">Never miss an expiry</p>
                  <p className="text-gray-500 dark:text-gray-400 text-sm">Get WhatsApp reminders before your tax, MOT or insurance expires</p>
                </div>
              </div>
              <Button
                className="bg-blue-600 hover:bg-blue-500 text-white shrink-0"
                onClick={() => setShowReminderModal(true)}
                data-testid="set-reminder-btn"
              >
                Set WhatsApp Reminder
              </Button>
            </div>

            {/* ===== UPSELL: Full Report (Locked) ===== */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-gray-50 dark:to-gray-950 z-10 pointer-events-none rounded-xl" />
              <Card className="bg-gray-100/60 dark:bg-gray-900/60 border-gray-300 dark:border-gray-700 overflow-hidden" data-testid="upsell-section">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-5">
                    <Lock className="h-5 w-5 text-yellow-500" />
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">We've already checked. The answers are here.</h3>
                  </div>
                  {/* Redacted data tease */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {[
                      { label: 'Stolen', value: '████████' },
                      { label: 'Outstanding Finance', value: '████████' },
                      { label: 'Write-off Record', value: '████████' },
                      { label: 'Previous Owners', value: '██' },
                      { label: 'Mileage Anomaly', value: '████████' },
                      { label: 'Vehicle Valuation', value: '£█,███' },
                      { label: 'Insurance Group', value: '██' },
                      { label: 'Full MOT History', value: '██ records' },
                      { label: 'Plate Changes', value: '████████' },
                    ].map((item) => (
                      <div key={item.label} className="py-2">
                        <p className="text-gray-400 dark:text-gray-500 text-xs mb-0.5">{item.label}</p>
                        <p className="text-gray-300 dark:text-gray-700 text-sm font-mono select-none blur-[2px]">{item.value}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* CTA over the locked section — paid purchase disabled, waitlist only */}
              <div className="absolute bottom-4 left-0 right-0 z-20 text-center px-4">
                <div className="bg-white/95 dark:bg-gray-900/95 border border-yellow-400/30 dark:border-yellow-500/30 rounded-xl p-6 max-w-md mx-auto backdrop-blur-sm shadow-lg dark:shadow-none text-left" data-testid="full-report-coming-soon">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="h-4 w-4 text-yellow-500" />
                    <p className="text-gray-900 dark:text-white font-bold text-base">Full history check: coming soon</p>
                  </div>
                  <p className="text-gray-500 dark:text-gray-400 text-xs mb-3">
                    Finance, stolen, write-off, previous owners, mileage anomalies and valuation — all in one report. Join the waitlist and we'll email you the moment it launches.
                  </p>
                  {user && (
                    <Button
                      className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold mb-3 w-full"
                      onClick={handleGenerateReport}
                      disabled={buyLoading}
                      data-testid="use-credit-btn"
                    >
                      Use 1 Credit (if you have one)
                    </Button>
                  )}
                  <WaitlistForm source="results" compact />
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ==================== BENEFITS (no result) ==================== */}
      {!result && (
        <section className="container mx-auto px-4 py-16">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white text-center mb-10" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              What you get — completely free
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { icon: Shield, title: 'Tax & MOT Status', desc: 'Live status with expiry dates from DVLA' },
                { icon: CalendarDays, title: 'Annual Tax Cost', desc: 'Know exactly what you\'ll pay each year' },
                { icon: Fuel, title: 'Estimated MPG', desc: 'Combined, urban & extra-urban figures' },
                { icon: Car, title: 'Vehicle Details', desc: 'Make, model, colour, engine, fuel type' },
                { icon: Gauge, title: 'CO2 Emissions', desc: 'Official emissions data in g/km' },
                { icon: Zap, title: 'Instant Results', desc: 'Under 2 seconds, no waiting' },
                { icon: Search, title: 'Unlimited Checks', desc: 'No daily limits, no sign-up needed' },
                { icon: Shield, title: 'Insurance Check', desc: 'Link to askMID official database' },
              ].map(({ icon: Icon, title, desc }) => (
                <div key={title} className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-5 text-center transition-colors">
                  <div className="bg-yellow-500/10 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Icon className="h-6 w-6 text-yellow-500" />
                  </div>
                  <h3 className="text-gray-900 dark:text-white font-semibold text-sm mb-1">{title}</h3>
                  <p className="text-gray-400 dark:text-gray-500 text-xs">{desc}</p>
                </div>
              ))}
            </div>

            {/* Pricing preview — paid reports disabled; show coming-soon + waitlist */}
            <div className="mt-16 text-center">
              <div className="max-w-xl mx-auto bg-white dark:bg-gray-900 border border-yellow-400/40 dark:border-yellow-500/30 rounded-2xl p-8 shadow-sm" data-testid="homepage-waitlist-card">
                <div className="inline-flex items-center gap-2 bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 text-xs font-semibold px-3 py-1 rounded-full mb-4">
                  <Sparkles className="h-3.5 w-3.5" />
                  Coming soon
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Full vehicle history check
                </h2>
                <p className="text-gray-500 dark:text-gray-400 mb-6 text-sm">
                  Finance, stolen, write-off, previous owners, mileage anomalies and valuation — in one straightforward report. We'll email you the moment it launches.
                </p>
                <ul className="grid grid-cols-2 gap-2 text-left mb-6 text-xs text-gray-600 dark:text-gray-400">
                  <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-yellow-500 rounded-full" />Outstanding finance</li>
                  <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-yellow-500 rounded-full" />Stolen check</li>
                  <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-yellow-500 rounded-full" />Write-off / Cat N or S</li>
                  <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-yellow-500 rounded-full" />Previous owners</li>
                  <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-yellow-500 rounded-full" />Mileage anomaly</li>
                  <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-yellow-500 rounded-full" />Live valuation</li>
                </ul>
                <WaitlistForm source="website" />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Blog articles strip */}
      {blogPosts.length > 0 && (
        <section className="container mx-auto px-4 py-12 border-t border-gray-200 dark:border-gray-800">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Guides & Advice
              </h2>
              <Link to="/blog" className="text-yellow-600 dark:text-yellow-500 text-sm font-medium hover:text-yellow-500 dark:hover:text-yellow-400 transition-colors flex items-center gap-1" data-testid="view-all-articles-link">
                View all <ChevronRight className="h-3 w-3" />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {blogPosts.map(post => (
                <Link key={post.slug} to={`/blog/${post.slug}`} data-testid={`landing-blog-${post.slug}`}>
                  <Card className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700 transition-all cursor-pointer h-full">
                    <CardContent className="p-5">
                      <h3 className="text-gray-900 dark:text-white font-semibold text-sm mb-2 line-clamp-2">{post.title}</h3>
                      <p className="text-gray-400 dark:text-gray-500 text-xs line-clamp-2">{post.meta_description}</p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
      <ChatWidget />
      {showReminderModal && result && (
        <SetReminderModal
          isOpen={showReminderModal}
          onClose={() => setShowReminderModal(false)}
          vehicle={result}
        />
      )}
      {showAuthModal && (
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => {
            setShowAuthModal(false);
            setAuthAction(null);
          }}
          onSuccess={() => {
            setShowAuthModal(false);
            setAuthAction(null);
          }}
        />
      )}
    </div>
  );
}

export default LandingPage;
